import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import axios from '../services/ApiService'
import DescriptionIcon from '@mui/icons-material/Description';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import DownloadIcon from '@mui/icons-material/Download';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import MenuItem from '@mui/material/MenuItem';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import GifBoxIcon from '@mui/icons-material/GifBox';
import MovieCreationIcon from '@mui/icons-material/MovieCreation';
import { alpha } from '@mui/material/styles';


const MP4_QUALITIES = [
    { value: "horizontal_480p", label: "Horizontal 480p" },
    { value: "horizontal_720p", label: "Horizontal 720p" },
    { value: "horizontal_1080p", label: "Horizontal 1080p" },
    { value: "horizontal_4k", label: "Horizontal 4K" },
    { value: "vertical_480p", label: "Vertical 480p" },
    { value: "vertical_720p", label: "Vertical 720p" },
    { value: "vertical_1080p", label: "Vertical 1080p" },
    { value: "vertical_4k", label: "Vertical 4K" },
];

const CANVA_DESIGN_TYPES = [
    {
        type: "doc",
        label: "Doc",
        desc: "A document for Canva's online text editor.",
        icon: <DescriptionIcon sx={{ fontSize: 48, color: "#2467e8" }} />
    },
    {
        type: "whiteboard",
        label: "Whiteboard",
        desc: "A whiteboard with infinite space to collaborate.",
        icon: <DashboardIcon sx={{ fontSize: 48, color: "#34bfa3" }} />
    },
    {
        type: "presentation",
        label: "Presentation",
        desc: "A presentation to collaborate and present.",
        icon: <SlideshowIcon sx={{ fontSize: 48, color: "#f7b731" }} />
    }
];

const canvaLogo = "https://img.icons8.com/fluency/100/canva.png";
const modalMinWidth = 340;
const modalMinHeight = 320;

const CanvaDesignModal = ({ open, onClose, onDesignTypeSelected, loadingDesigns, canvaDesigns }) => {
    const [step, setStep] = useState('choose'); // 'choose' | 'ready' | 'loading'
    const [checking, setChecking] = useState(false);
    const [exportDialog, setExportDialog] = useState({
        open: false,
        designId: null,
        formats: [],
        selectedFormat: null,
        action: null
    });
    const [exportBusy, setExportBusy] = useState(false);
    const [busyAction, setBusyAction] = useState(null); // 'download' | 'save' | null
    const [busy, setBusy] = useState(false);

    const withBusy = async (action, fn) => {
        setBusyAction(action);
        setBusy(true);
        try { await fn(); }
        finally { setBusy(false); setBusyAction(null); }
    };

    const FORMAT_ICON = {
        pdf: <PictureAsPdfIcon sx={{ fontSize: 18 }} />,
        jpg: <ImageIcon sx={{ fontSize: 18 }} />,
        png: <ImageIcon sx={{ fontSize: 18 }} />,
        gif: <GifBoxIcon sx={{ fontSize: 18 }} />,
        mp4: <MovieCreationIcon sx={{ fontSize: 18 }} />
    };

    const formatChipSx = (selected) => ({
        borderRadius: 999,
        px: 2,
        py: 1,
        mr: 1,
        mb: 1,
        fontWeight: 700,
        textTransform: 'none',
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: selected ? '#2086F6' : '#E6EEF7',
        color: selected ? '#fff' : '#1C2A3A',
        background: selected ? '#2086F6' : '#fff',
        '&:hover': {
            background: selected ? alpha('#2086F6', 0.9) : '#F4F8FF',
            borderColor: selected ? '#2086F6' : '#DCE8F8'
        }
    });

    const [designTypeModal, setDesignTypeModal] = useState(false);
    const [formatOptions, setFormatOptions] = useState({});
    const displayedDesigns = canvaDesigns.slice(0, 10);

    const [savingLibraryId, setSavingLibraryId] = useState(null);
    const [saveLibrarySuccess, setSaveLibrarySuccess] = useState(null);

    const ALLOWED_FORMATS = new Set(["jpg", "png", "gif", "pdf"]);

    function filterExportFormats(raw: any[]): { type: string; label?: string }[] {
        if (!Array.isArray(raw)) return [];
        return raw
            .map(f => (typeof f === "string" ? { type: f } : f))
            .filter(f => f && typeof f.type === "string")
            .filter(f => ALLOWED_FORMATS.has(f.type.toLowerCase()))
            .map(f => ({ ...f, label: f.label ?? f.type.toUpperCase() }));
    }

    useEffect(() => {
        if (!open) {
            setStep('choose');
            return;
        }
        setChecking(true);
        checkCanvaConnected().then(isConnected => {
            setStep(isConnected ? 'ready' : 'choose');
            setChecking(false);
        });
    }, [open]);

    const handleSaveFormatToLibrary = async () => {
        const { designId, selectedFormat } = exportDialog;
        if (!designId || !selectedFormat) return;

        setExportBusy(true);
        setSavingLibraryId(designId);
        setSaveLibrarySuccess(null);

        try {
            let dto = {
                designId,
                formatType: selectedFormat.type,
                formatOptions: { ...formatOptions }
            };

            // You can customize more formatOptions as you like!
            if (selectedFormat.type === "jpg") {
                if (!dto.formatOptions.quality) dto.formatOptions.quality = 90;
            }

            // 1. Export the design in the chosen format
            const exportRes = await axios.post("/channel/exportDesign", dto);
            const exportJobId = exportRes.data.exportJobId;
            let url = null, tries = 0;
            while (!url && tries < 10) {
                await new Promise(r => setTimeout(r, 1200));
                const exportUrlRes = await axios.get(`/channel/exportDesignUrl?designId=${designId}&exportJobId=${exportJobId}`);
                url = exportUrlRes.data.url;
                tries++;
            }
            if (!url) throw new Error("Failed to get export URL");

            // 2. Save to media library
            const design = canvaDesigns.find(d => d.id === designId);
            const payload = {
                Title: design?.title || "Untitled",
                Source: "canva",
                Url: url,
                ThumbnailUrl: design?.thumbnailUrl,
                Type: selectedFormat.type === "mp4" ? "video" : "image",
                OriginalId: designId,
            };

            const saveRes = await axios.post("/mediaLibrary/save", payload);
            setSaveLibrarySuccess(prev => ({ ...prev, [designId]: true }));

            setExportDialog({ ...exportDialog, open: false }); // Close dialog
        } catch (err) {
            setSaveLibrarySuccess(prev => ({ ...prev, [exportDialog.designId]: false }));
            alert("Save to library failed. " + (err.response?.data?.message || err.message));
        } finally {
            setSavingLibraryId(null);
            setExportBusy(false);
        }
    };

    const handleSaveClick = async (design) => {
        try {
            const exportFormatsRes = await axios.get(`/channel/getExportFormats?designId=${design.id}`);
            //const formats = exportFormatsRes.data.formats || [];
            const formats = filterExportFormats(exportFormatsRes.data?.formats || []);

            setExportDialog({
                open: true,
                designId: design.id,
                formats,
                selectedFormat: null,
                action: 'save'
            });
            setFormatOptions({});
        } catch (err) {
            alert("Failed to fetch export formats for saving.");
            console.error("[Canva] Failed to fetch export formats:", err);
        }
    };

    async function checkCanvaConnected() {
        //const res = await fetch("/Identity/Account/ExternalLogin/CheckExternalClaim?providerName=Canva");
        //if (!res.ok) return false;
        //const data = await res.json();
        //return data === true || !!data.accessToken;

        try {
            const res = await axios.get("/channel/isConnected");
            if (res.data && typeof res.data.isConnected !== 'undefined') {
                return res.data.isConnected;
            }
            return false;
        } catch (err) {
            console.error("[Canva] Error checking Canva connection:", err);
            return false;
        }
    }

    async function downloadFileBlob(url, filename = 'Canva_Design.png') {
        try {
            const response = await fetch(url, {
                mode: 'cors', 
                credentials: 'omit'
            });
            if (!response.ok) throw new Error("Network response was not ok");
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            }, 100);
        } catch (err) {
            alert("Download failed. Try again.");
        }
    }

    const handleDownloadClick = async (designId, designTitle) => {
        try {
            const res = await axios.get(`/channel/getExportFormats?designId=${designId}`);
            const formats = filterExportFormats(res.data?.formats || []);

            setExportDialog({
                open: true,
                designId,
                formats,
                selectedFormat: null,
                action: 'download',
            });
            setFormatOptions({});
        } catch (err) {
            alert("Failed to fetch export formats.");
            console.error("[Canva] Failed to fetch export formats:", err);
        }
    };

    const handleFormatSelect = (fmt) => {
        setExportDialog(prev => ({
            ...prev,
            selectedFormat: fmt
        }));
        if (fmt.type === "jpg") {
            setFormatOptions({ quality: 90 });
        } else if (fmt.type === "pdf") {
            setFormatOptions({ export_quality: "regular" });
        } else if (fmt.type === "mp4") {
            setFormatOptions({ quality: "horizontal_720p" });
        } else {
            setFormatOptions({});
        }
    };

    const handleOptionChange = (field, value) => {
        setFormatOptions(opts => ({ ...opts, [field]: value }));
    };

    async function handleDownloadDesign() {
        const { designId, selectedFormat } = exportDialog;
        if (!designId || !selectedFormat) return;
        setExportBusy(true);

        try {
            let dto = {
                designId,
                formatType: selectedFormat.type,
                formatOptions: { ...formatOptions }
            };

            if (selectedFormat.type === "jpg") {
                if (!dto.formatOptions.quality) dto.formatOptions.quality = 90;
                // may want to allow specifying width/height as well!
            }

            const res = await axios.post("/channel/exportDesign", dto);
            const { exportJobId } = res.data;
            let url = null, tries = 0;
            while (!url && tries < 10) {
                await new Promise(r => setTimeout(r, 1200));
                const res2 = await axios.get(`/channel/exportDesignUrl?designId=${designId}&exportJobId=${exportJobId}`);
                url = res2.data.url;
                tries++;
            }
            if (url) {
                const ext = selectedFormat.type || "png";
                let safeTitle = "Canva_Design";
                if (canvaDesigns && designId) {
                    const found = canvaDesigns.find(d => d.id === designId);
                    if (found && found.title) {
                        safeTitle = found.title.replace(/[^\w\s.-]/g, "_");
                    }
                }
                await downloadFileBlob(url, `${safeTitle}.${ext}`);
                setExportDialog({ open: false, designId: null, formats: [], selectedFormat: null });
            } else {
                alert("Failed to export design. Please try again.");
            }

        } catch (err) {
            alert("Export failed.");
        } finally {
            setExportBusy(false);
        }
    }

    function handleCanvaLogin() {
        setStep('loading');
        var form = document.createElement('form');
        form.setAttribute('method', 'get');
        form.setAttribute('action', '/Identity/Account/ExternalLogin/ExternalClaimProvider?provider=Canva');
        form.style.display = 'none';
        var returnUrlInput = document.createElement('input');
        returnUrlInput.setAttribute('type', 'hidden');
        returnUrlInput.setAttribute('name', 'returnUrl');
        returnUrlInput.setAttribute('value', window.location.pathname + window.location.search);
        form.appendChild(returnUrlInput);
        var providerInput = document.createElement('input');
        providerInput.setAttribute('type', 'hidden');
        providerInput.setAttribute('name', 'provider');
        providerInput.setAttribute('value', 'Canva');
        form.appendChild(providerInput);
        document.body.appendChild(form);
        form.submit();
        setTimeout(() => form.remove(), 100);
    }

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            //maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: "#fff",
                    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.12)",
                    minWidth: modalMinWidth,
                    minHeight: modalMinHeight,
                    p: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }
            }}
        >
            
            {checking ? (
                <Box
                    sx={{
                        width: modalMinWidth,
                        height: modalMinHeight,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                    }}
                >
                    <CircularProgress sx={{ color: "#2086F6" }} size={44} thickness={3} />
                </Box>
            ) : (
                <DialogContent
                    sx={{
                        p: 0,
                        //width: modalMinWidth,
                        minHeight: modalMinHeight,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        justifyContent: "center"
                    }}
                >
                    <Box
                        sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: modalMinHeight,
                        }}
                    >
                        <Avatar
                            src={canvaLogo}
                            alt="Canva"
                            sx={{
                                width: 56, height: 56,
                                bgcolor: "#E3F0FB",
                                mb: 2
                            }}
                        />
                        <Typography
                            fontWeight={700}
                            sx={{
                                fontSize: "1.3rem",
                                color: "#212121",
                                mb: 2,
                                textAlign: "center",
                                letterSpacing: 0.1
                            }}
                        >
                            Log in with Canva
                        </Typography>

                        {step === "choose" && (
                            <>
                                <Button
                                    disableElevation
                                    variant="contained"
                                    onClick={handleCanvaLogin}
                                    sx={{
                                        background: "#2086F6",
                                        color: "white",
                                        minWidth: 220,
                                        minHeight: 48,
                                        borderRadius: 2,
                                        fontWeight: 700,
                                        fontSize: "1.08rem",
                                        textTransform: "none",
                                        mb: 1.8,
                                        boxShadow: "none",
                                        "&:hover": {
                                            background: "#176CD3"
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.2,
                                    }}
                                    startIcon={
                                        <Avatar
                                            src={canvaLogo}
                                            alt="Canva"
                                            sx={{
                                                width: 32, height: 32, bgcolor: "#fff"
                                            }}
                                        />
                                    }
                                >
                                    Continue with Canva
                                </Button>
                                <Typography
                                    sx={{
                                        fontSize: "0.98rem",
                                        color: "#606770",
                                        textAlign: "center",
                                        maxWidth: 270,
                                        mb: 0.5
                                    }}
                                >
                                    Use Canva’s editor to design and import your post.
                                </Typography>
                            </>
                        )}

                        {step === "loading" && (
                            <>
                                <CircularProgress sx={{ color: "#2086F6", mb: 2 }} size={36} thickness={3} />
                                <Typography
                                    fontWeight={500}
                                    sx={{ textAlign: "center", color: "#2086F6", fontSize: "1.02rem" }}
                                >
                                    Redirecting to Canva...
                                </Typography>
                            </>
                        )}

                            {step === "ready" && (
                                <>
                                    <Typography
                                        sx={{
                                            color: "#606770",
                                            textAlign: "center",
                                            fontSize: "1.03rem",
                                            mb: 2
                                        }}
                                    >
                                        Canva is connected. Start designing your content in a new tab, or use an existing design below.
                                    </Typography>
                                    <Button
                                        onClick={() => setDesignTypeModal(true)}
                                        variant="contained"
                                        sx={{
                                            background: "#2086F6",
                                            color: "white",
                                            minWidth: 180,
                                            minHeight: 44,
                                            borderRadius: 2,
                                            fontWeight: 700,
                                            fontSize: "1.07rem",
                                            textTransform: "none",
                                            boxShadow: "none",
                                            "&:hover": {
                                                background: "#176CD3"
                                            }
                                        }}
                                    >
                                        Start Designing
                                    </Button>

                                    {/* Gallery Section */}
                                    <Box sx={{ mt: 3, width: '100%', textAlign: 'left' }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1, color: "#192841" }}>
                                            Your Recent Canva Designs
                                        </Typography>
                                        {loadingDesigns ? (
                                            <Box display="flex" justifyContent="center" p={4}>
                                                <CircularProgress />
                                            </Box>
                                        ) : displayedDesigns.length === 0 ? (
                                            <Box display="flex" flexDirection="column" alignItems="center" p={5} color="#B0BAC9">
                                                <img
                                                    src="https://cdn-icons-png.flaticon.com/512/2748/2748558.png"
                                                    alt="No designs"
                                                    style={{ width: 60, marginBottom: 14, opacity: 0.7 }}
                                                />
                                                <Typography fontWeight={500} fontSize="1.08rem">
                                                    No Canva designs found.
                                                </Typography>
                                                <Typography fontSize="0.95rem" mt={0.8} color="#8596AE">
                                                    Start designing to see your creations here!
                                                </Typography>
                                            </Box>
                                        ) : (
                                                        <Box
                                                            sx={{
                                                                display: 'grid',
                                                                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                                                                gap: 2.5,
                                                                py: 1,
                                                                px: 0.5,
                                                                maxHeight: 370,
                                                                overflowY: 'auto',
                                                                overflowX: 'hidden',
                                                                minHeight: 160,
                                                                scrollbarWidth: "thin",
                                                                '&::-webkit-scrollbar': { height: 8, background: "#f5f7fa" },
                                                                '&::-webkit-scrollbar-thumb': { background: "#d9e2ee", borderRadius: 12 },
                                                            }}
                                                        >
                                                            {displayedDesigns.map(design => (
                                                                <Box
                                                                    key={design.id}
                                                                    sx={{
                                                                        display: "flex",
                                                                        flexDirection: "column",
                                                                        alignItems: "stretch",
                                                                        background: "rgba(255,255,255,0.88)",
                                                                        border: '1.2px solid #e6eef7',
                                                                        borderRadius: 3,
                                                                        p: 2,
                                                                        minWidth: 0,
                                                                        minHeight: 242,
                                                                        boxShadow: "0 4px 24px 0 rgba(32,134,246,0.07)",
                                                                        transition: "transform .16s, box-shadow .18s",
                                                                        position: "relative",
                                                                        cursor: 'pointer',
                                                                        overflow: 'hidden',
                                                                        "&:hover": {
                                                                            transform: "translateY(-4px) scale(1.045)",
                                                                            boxShadow: "0 12px 32px 0 #2086f630",
                                                                            backdropFilter: "blur(2.5px)"
                                                                        }
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            width: "100%",
                                                                            aspectRatio: "1/1",
                                                                            borderRadius: 2,
                                                                            overflow: "hidden",
                                                                            bgcolor: "#f3f8fd",
                                                                            mb: 1.5,
                                                                            boxShadow: "0 2px 16px #dbedfa40",
                                                                            position: "relative"
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={design.thumbnailUrl}
                                                                            alt={design.title}
                                                                            style={{
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                objectFit: "cover",
                                                                                display: "block",
                                                                                background: "#f3f8fd"
                                                                            }}
                                                                            onError={e => { e.target.src = "https://cdn-icons-png.flaticon.com/512/833/833281.png"; }}
                                                                        />
                                                                    </Box>
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: '1rem',
                                                                            color: "#17253f",
                                                                            fontWeight: 700,
                                                                            mb: 0.5,
                                                                            textOverflow: "ellipsis",
                                                                            overflow: "hidden",
                                                                            whiteSpace: "nowrap",
                                                                            maxWidth: "100%",
                                                                            textAlign: "center",
                                                                            letterSpacing: 0.02
                                                                        }}
                                                                        title={design.title}
                                                                    >
                                                                        {design.title}
                                                                    </Typography>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            flexDirection: 'row',
                                                                            gap: 1, // 8px gap
                                                                            width: '100%',
                                                                            mt: 'auto',
                                                                            pb: 0.5,
                                                                            minHeight: 40,
                                                                        }}
                                                                    >
                                                                        {/* Download Button */}
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                height: 40,
                                                                                borderRadius: '14px 0 0 14px', // Round left only
                                                                                color: "#1b4fd5",
                                                                                borderColor: "#dde7f7",
                                                                                bgcolor: "transparent",
                                                                                '&:hover': {
                                                                                    background: "#f0f7ff",
                                                                                    borderColor: "#2086F6"
                                                                                },
                                                                                boxShadow: "none"
                                                                            }}
                                                                            onClick={() => handleDownloadClick(design.id)}
                                                                        >
                                                                            <DownloadIcon sx={{ fontSize: 19, mr: 0.6 }} />
                                                                        </Button>
                                                                        {/* Save Button */}
                                                                        {saveLibrarySuccess?.[design.id] ? (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                disabled
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    minWidth: 0,
                                                                                    height: 40,
                                                                                    borderRadius: '0 14px 14px 0', // Round right only
                                                                                    fontWeight: 700,
                                                                                    bgcolor: "#32cf92",
                                                                                    color: "#fff",
                                                                                    boxShadow: "none"
                                                                                }}
                                                                                startIcon={<LibraryAddCheckIcon sx={{ fontSize: 18 }} />}
                                                                            >
                                                                                Saved!
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                sx={{
                                                                                    flex: 1,
                                                                                    minWidth: 0,
                                                                                    height: 40,
                                                                                    borderRadius: '0 14px 14px 0', // Round right only
                                                                                    fontWeight: 700,
                                                                                    background: "#2086F6",
                                                                                    color: "#fff",
                                                                                    textTransform: "none",
                                                                                    boxShadow: "none",
                                                                                    '&:hover': { background: "#176CD3" },
                                                                                    position: 'relative'
                                                                                }}
                                                                                startIcon={
                                                                                    savingLibraryId === design.id
                                                                                        ? <CircularProgress size={17} sx={{ color: "#fff" }} />
                                                                                        : <SaveAltIcon sx={{ fontSize: 17 }} />
                                                                                }
                                                                                onClick={() => handleSaveClick(design)}
                                                                                disabled={!!savingLibraryId}
                                                                            >
                                                                                {savingLibraryId === design.id ? "" : "Save"}
                                                                            </Button>
                                                                        )}
                                                                    </Box>

                                                                </Box>
                                                            ))}
                                                        </Box>

                                        )}
                                    </Box>

                                </>
                            )}

                    </Box>
                </DialogContent>
            )}
            </Dialog>
            <Dialog
                open={designTypeModal}
                onClose={() => setDesignTypeModal(false)}
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: "#fafcff",
                        boxShadow: "0 8px 40px 0 rgba(31, 38, 135, 0.16)",
                        p: 0,
                        minWidth: 380,
                    }
                }}
            >
                <DialogContent sx={{ p: 4 }}>
                    <Typography fontWeight={700} fontSize="1.25rem" mb={2} textAlign="center">
                        Choose a Design Type
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 2.5,
                            justifyContent: "center",
                            alignItems: "stretch",
                        }}
                    >
                        {CANVA_DESIGN_TYPES.map(dt => (
                            <Button
                                key={dt.type}
                                variant="outlined"
                                sx={{
                                    minWidth: 120,
                                    flex: 1,
                                    flexDirection: "column",
                                    borderRadius: 3,
                                    borderWidth: 2,
                                    borderColor: "#e6eef7",
                                    py: 2.2,
                                    px: 2,
                                    bgcolor: "#fff",
                                    boxShadow: "0 2px 14px 0 #2467e810",
                                    '&:hover': {
                                        borderColor: "#2467e8",
                                        background: "#f0f6ff",
                                        boxShadow: "0 6px 24px 0 #2467e822"
                                    },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.2
                                }}
                                onClick={() => {
                                    setDesignTypeModal(false);
                                    if (typeof onDesignTypeSelected === "function") {
                                        onDesignTypeSelected(dt.type);
                                    }
                                }}
                            >
                                {dt.icon}
                                <Typography fontWeight={700} fontSize="1.05rem" mt={1} mb={0.5} color="#192841">
                                    {dt.label}
                                </Typography>
                                <Typography fontSize="0.97rem" color="#607089" sx={{ minHeight: 34 }}>
                                    {dt.desc}
                                </Typography>
                            </Button>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
            <Dialog
                open={exportDialog.open}
                onClose={() => setExportDialog({ ...exportDialog, open: false })}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        boxShadow: '0 18px 64px rgba(32,134,246,0.20)'
                    }
                }}
            >
                <DialogTitle sx={{ px: 3, py: 2.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: "column", alignItems: 'left', gap: 1 , ml: 2}}>
                        <Typography variant="h6" fontWeight={800}>
                            {exportDialog.action === 'save' ? 'Export & Save to Library' : 'Export & Download'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary'}}>
                            Choose a format and tweak quality before exporting.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={() => setExportDialog({ ...exportDialog, open: false })}
                        sx={{ position: 'absolute', right: 12, top: 10 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 0, pb: 2.5 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 3,
                            px: 3
                        }}
                    >
                        {/* LEFT: format selector */}
                        <Box
                            sx={{
                                border: '1px solid #EEF3FB',
                                borderRadius: 3,
                                p: 2,
                                bgcolor: '#FBFDFF'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 800, color: '#1B2B46' }}>
                                Select Export Format
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                {exportDialog.formats.map((fmt) => {
                                    const selected = exportDialog.selectedFormat?.type === fmt.type;
                                    return (
                                        <Button
                                            key={fmt.type}
                                            variant={selected ? 'contained' : 'outlined'}
                                            onClick={() => handleFormatSelect(fmt)}
                                            startIcon={FORMAT_ICON[fmt.type?.toLowerCase()] || null}
                                            sx={formatChipSx(selected)}
                                        >
                                            {fmt.label || fmt.type?.toUpperCase()}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* RIGHT: contextual options */}
                        <Box
                            sx={{
                                border: '1px solid #EEF3FB',
                                borderRadius: 3,
                                p: 2,
                                bgcolor: '#FFFFFF'
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 800, color: '#1B2B46' }}>
                                Options
                            </Typography>

                            {/* JPG */}
                            {exportDialog.selectedFormat?.type === 'jpg' && (
                                <Box sx={{ my: 1 }}>
                                    <Typography sx={{ mb: 1, color: '#4B5B76' }}>
                                        JPG Quality: <b>{formatOptions.quality || 90}</b>
                                    </Typography>
                                    <Slider
                                        value={formatOptions.quality || 90}
                                        onChange={(_, v) =>
                                            handleOptionChange('quality', Array.isArray(v) ? v[0] : v)
                                        }
                                        min={1}
                                        max={100}
                                        step={1}
                                        sx={{ width: '100%' }}
                                    />
                                </Box>
                            )}

                            {/* PDF */}
                            {exportDialog.selectedFormat?.type === 'pdf' && (
                                <Box sx={{ my: 1 }}>
                                    <Typography sx={{ mb: 1, color: '#4B5B76' }}>Quality</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            onClick={() => handleOptionChange('export_quality', 'regular')}
                                            variant={formatOptions.export_quality === 'regular' ? 'contained' : 'outlined'}
                                            sx={formatChipSx(formatOptions.export_quality === 'regular')}
                                        >
                                            Regular
                                        </Button>
                                        <Button
                                            onClick={() => handleOptionChange('export_quality', 'pro')}
                                            variant={formatOptions.export_quality === 'pro' ? 'contained' : 'outlined'}
                                            sx={formatChipSx(formatOptions.export_quality === 'pro')}
                                        >
                                            Pro
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* MP4 */}
                            {exportDialog.selectedFormat?.type === 'mp4' && (
                                <Box sx={{ my: 1 }}>
                                    <Typography sx={{ mb: 1, color: '#4B5B76' }}>MP4 Quality</Typography>
                                    <Select
                                        fullWidth
                                        value={formatOptions.quality || 'horizontal_720p'}
                                        onChange={(e) => handleOptionChange('quality', e.target.value)}
                                        sx={{ borderRadius: 2 }}
                                    >
                                        {MP4_QUALITIES.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}

                            {!exportDialog.selectedFormat && (
                                <Typography sx={{ color: '#7E8AA8' }}>
                                    Pick a format on the left to see its options.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: '1px solid #EEF3FB',
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: '#fff',
                        zIndex: 1
                    }}
                >
                    <Button
                        onClick={() => setExportDialog({ ...exportDialog, open: false })}
                        sx={{ textTransform: 'none', fontWeight: 700, color: '#5A6B88' }}
                        disabled={busy}
                    >
                        Cancel
                    </Button>

                    {exportDialog.action === 'download' ? (
                        <Button
                            variant="contained"
                            disabled={!exportDialog.selectedFormat || busy}
                            onClick={() => withBusy('download', handleDownloadDesign)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                fontWeight: 800,
                                textTransform: 'none',
                                background: '#2086F6',
                                boxShadow: 'none',
                                '&:hover': { background: '#176CD3' },
                                position: 'relative',
                                minWidth: 140
                            }}
                            startIcon={
                                busyAction === 'download' ? (
                                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                                ) : (
                                    <DownloadIcon sx={{ fontSize: 18 }} />
                                )
                            }
                        >
                            {busyAction === 'download' ? 'Exporting…' : 'Download'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={!exportDialog.selectedFormat || busy}
                            onClick={() => withBusy('save', handleSaveFormatToLibrary)}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                fontWeight: 800,
                                textTransform: 'none',
                                background: '#2086F6',
                                boxShadow: 'none',
                                '&:hover': { background: '#176CD3' },
                                position: 'relative',
                                minWidth: 160
                            }}
                            startIcon={
                                busyAction === 'save' ? (
                                    <CircularProgress size={18} sx={{ color: '#fff' }} />
                                ) : (
                                    <SaveAltIcon sx={{ fontSize: 18 }} />
                                )
                            }
                        >
                            {busyAction === 'save' ? 'Saving…' : 'Save to Library'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
 
    </>
    );
};

export default CanvaDesignModal;
